import NewAddFundsModal from '@efex-components/payables/addFundModal/NewAddFundsModal';
import { Button } from '@efexteam/components';
import { Actions, Can, Subjects } from '@efexteam/permission-manager';
import FilterAltIcon from '@mui/icons-material/FilterAlt';
import FilterAltOffIcon from '@mui/icons-material/FilterAltOff';
import { Box, Pagination, Stack, useMediaQuery } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useNavigate, useSearchParams } from 'react-router-dom';
import RECEIVABLES_TABS from '@/modules/receivables/constants/ReceivablesTable.constants';
import RECEIVABLES_TABS_NUMBER from '@/modules/receivables/constants/ReceivableTabs.constants';
import { missingReceivablesImage } from '../../assets';
import ErrorRetry from '../../components/common/ErrorRetry';
import OutletTableSkeleton from '../../components/common/Skeleton/OutletTableSkeleton';
import FilterByDate from '../../components/filters/FilterByDate';
import SearchTools from '../../components/searchTools/SearchTools';
import useBoolean from '../../hooks/useBoolean';
import OutletLayout from '../../layout/OutletLayout';
import ReceivablesTable from '../../modules/receivables/ReceivablesTable';
import {
  ReceivableResponse,
  ReceivablesResponse,
} from '../../services/receivables/types';
import { QueryParams } from '../../services/types';
import MissingInfo from '../missing/MissingInfo';
import { selectProfileData } from '../profile/store/selectors';

interface ReceivableProps {
  data: ReceivablesResponse;
  loading: boolean;
  error: Error | undefined;
  movementType: string;
  getReceivables: (params?: any) => void;
  setMovementType: (type: keyof typeof RECEIVABLES_TABS) => void;
}

const Receivables = ({
  data,
  loading,
  error,
  movementType,
  getReceivables,
  setMovementType,
}: ReceivableProps) => {
  const theme = useTheme();
  const matches = useMediaQuery(theme.breakpoints.down('sm'));
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [searchParams, setSearchParams] = useSearchParams();
  const [currentPage, setCurrentPage] = useState(1);
  const userProfile = useSelector(selectProfileData);
  const [receivables, setDataInfo] = useState<ReceivableResponse[] | null>(
    null
  );
  const [params, setParams] = useState<QueryParams>({});

  const [showingFilters, { setFalse: removeFilters, setTrue: showFilters }] =
    useBoolean(false);
  const [
    isAddFundsModalOpen,
    { setTrue: showAddFundsModal, setFalse: hideAddFundsModal },
  ] = useBoolean(false);

  const openPopupDocumentKYB = () => {
    navigate('/onboarding');
  };

  const openPopupAddFunds = () => {
    if (!userProfile?.isFullRegister) {
      openPopupDocumentKYB();
      return;
    }
    showAddFundsModal();
  };

  const handleFilters = () => {
    if (showingFilters) {
      removeFilters();
    } else {
      showFilters();
    }
  };

  const filterByDates = (values: any) => {
    const { dateFrom, dateTo } = values;
    setParams({ ...params, dateFrom, dateTo });
  };

  const handleChangePage = (page: number) => {
    if (page === currentPage) return;
    setCurrentPage(page);
    setParams({ ...params, page });
  };

  useEffect(() => {
    getReceivables(params);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [movementType, params]);

  useEffect(() => {
    const openRequestModal = searchParams.get('action') === 'new_invoice';
    const tab = searchParams.get('tab');
    if (tab) {
      setMovementType(tab as keyof typeof RECEIVABLES_TABS);
      searchParams.delete('tab');
      setSearchParams(searchParams);
    }
    if (openRequestModal) {
      showAddFundsModal();
    }
    setDataInfo(data?.results || []);
  }, [data, searchParams, showAddFundsModal, setMovementType, setSearchParams]);

  const getInitialTab = () =>
    RECEIVABLES_TABS_NUMBER[
      movementType as keyof typeof RECEIVABLES_TABS_NUMBER
    ];

  if (error) {
    return <ErrorRetry onRetry={() => getReceivables(params)} />;
  }

  const renderFilters = (showRequestButton = true) => (
    <>
      <Stack
        direction={{ xs: 'column-reverse', md: 'row' }}
        justifyContent="space-between"
        alignItems="center"
      >
        <Stack
          direction="row"
          justifyContent="center"
          alignItems="flex-end"
          spacing={2}
          className="mb-5 md:min-w-[550px]"
        >
          <SearchTools
            onChange={(search) => setParams({ ...params, search })}
          />
          {!matches && (
            <Button
              className="!mb-1 w-full max-w-40"
              appearance="primary"
              onClick={handleFilters}
            >
              {!showingFilters ? t('addFilters') : t('hideFilters')}
            </Button>
          )}
          {matches && (
            <Button
              className="!mb-1"
              appearance="primary"
              onClick={handleFilters}
            >
              {!showingFilters ? <FilterAltIcon /> : <FilterAltOffIcon />}
            </Button>
          )}
        </Stack>
        {showRequestButton && (
          <Can action={Actions.CREATE} subject={Subjects.ADD_FUNDS}>
            <Button
              variant="outlined"
              appearance="primary"
              onClick={openPopupAddFunds}
            >
              {t('newAccountNumberButton')}
            </Button>
          </Can>
        )}
      </Stack>
      {showingFilters && <FilterByDate onChange={filterByDates} />}
    </>
  );

  const renderContent = () => (
    <Box>
      {renderFilters()}
      <Box style={{ minHeight: '50vh' }}>
        {(loading || !data) && <OutletTableSkeleton />}
        {!loading && receivables && (
          <>
            <ReceivablesTable
              receivables={receivables || []}
              setMovementType={(type) => setMovementType(type)}
              initalTab={getInitialTab()}
            />
            {movementType !== RECEIVABLES_TABS.COLLECT_LOCAL_PAYMENT ? (
              <Box className="flex justify-center p-4 text-center w-full">
                <Pagination
                  page={currentPage}
                  color="primary"
                  count={data?.total_pages}
                  onChange={(_: any, page: number) => handleChangePage(page)}
                />
              </Box>
            ) : null}
          </>
        )}
        {!loading && (!receivables || !receivables.length) && (
          <Box>
            <MissingInfo
              boxTitle=""
              image={missingReceivablesImage}
              messageTitle={t('missingDataReceivablesTitle')}
              messageText={t('missingDataReceivablesText')}
            />
          </Box>
        )}
      </Box>
    </Box>
  );

  return (
    <OutletLayout title={t('receivables') || ''} fullWidth>
      {renderContent()}

      {isAddFundsModalOpen ? (
        <NewAddFundsModal
          open={isAddFundsModalOpen}
          onClose={hideAddFundsModal}
        />
      ) : null}
    </OutletLayout>
  );
};

export default Receivables;
